import { Plus, Trash2, Check } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils/cn'
import { useCltWizardStore } from '@/stores/useCltWizardStore'
import { useStockingStrategyCodes } from '@/hooks/useCltReference'
import { CltStepHeader } from './CltStepHeader'
import { suggestGroupLabel, type StrategyGroup } from './clt.mock'

/**
 * Step: edit the matrix COLUMNS (stocking-strategy groups). The grouping is
 * per-template: the user can add/remove groups and move codes between them.
 * Defaults to Seasonal 90 / SBA 1 / SBA 3,4,7. A code lives in one group at a
 * time, so assigning it here removes it from any other group.
 */
export function StepStrategyGroups() {
  const strategyGroups = useCltWizardStore(s => s.strategyGroups)
  const addStrategyGroup = useCltWizardStore(s => s.addStrategyGroup)
  const removeStrategyGroup = useCltWizardStore(s => s.removeStrategyGroup)
  const setStrategyGroupLabel = useCltWizardStore(s => s.setStrategyGroupLabel)
  const setStrategyGroupCodes = useCltWizardStore(s => s.setStrategyGroupCodes)
  const { data: strategyCodes = [] } = useStockingStrategyCodes()

  /** Which group currently owns each code (for the "taken by" hint). */
  const ownerOf = (code: string) =>
    strategyGroups.find(g => g.codes.includes(code))

  const toggleCode = (group: StrategyGroup, code: string) => {
    const has = group.codes.includes(code)
    const nextCodes = has
      ? group.codes.filter(c => c !== code)
      : [...group.codes, code]
    setStrategyGroupCodes(group.id, nextCodes)
    // Keep an un-customised label in sync with its codes.
    const labelIsAuto =
      group.label.trim() === '' ||
      group.label === 'New group' ||
      group.label === suggestGroupLabel(group.codes)
    if (labelIsAuto)
      setStrategyGroupLabel(group.id, suggestGroupLabel(nextCodes))
  }

  return (
    <div className="mx-auto w-full max-w-[900px]">
      <CltStepHeader
        title="Stocking Strategy Groups"
        description="These become the columns of the milestone matrix. Group one or more strategy codes per column, and add or remove groups as needed. Defaults to Seasonal 90, SBA 1, and SBA 3, 4, 7."
      />

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-foreground">
            {strategyGroups.length} group
            {strategyGroups.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={addStrategyGroup}
            className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          >
            <Plus className="h-3.5 w-3.5" />
            Add group
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {strategyGroups.map(group => (
            <div
              key={group.id}
              className="flex w-[260px] flex-col gap-2 rounded-md border border-border bg-background p-2.5"
            >
              <div className="flex items-center gap-1.5">
                <Input
                  value={group.label}
                  placeholder="Group name"
                  onChange={e =>
                    setStrategyGroupLabel(group.id, e.target.value)
                  }
                  className="h-8 text-sm"
                />
                <button
                  type="button"
                  onClick={() => removeStrategyGroup(group.id)}
                  disabled={strategyGroups.length <= 1}
                  title={
                    strategyGroups.length <= 1
                      ? 'A template needs at least one group'
                      : 'Remove this group'
                  }
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex min-h-[34px] cursor-pointer flex-wrap items-center gap-1 rounded-md border border-dashed border-border px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/40"
                  >
                    {group.codes.length === 0 ? (
                      <span className="flex items-center gap-1">
                        <Plus className="h-3 w-3" /> Add codes
                      </span>
                    ) : (
                      group.codes.map(code => (
                        <span
                          key={code}
                          className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary"
                        >
                          {code}
                        </span>
                      ))
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[260px] p-0">
                  <div className="border-b border-border px-3 py-2">
                    <h4 className="text-xs font-semibold text-foreground">
                      Strategy codes in this group
                    </h4>
                  </div>
                  <div className="max-h-[240px] overflow-y-auto p-2">
                    {strategyCodes.map(({ code, label }) => {
                      const selected = group.codes.includes(code)
                      const owner = ownerOf(code)
                      const takenElsewhere = !selected && owner
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => toggleCode(group, code)}
                          className={cn(
                            'flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                            selected
                              ? 'bg-primary/10 text-foreground'
                              : 'hover:bg-muted/60'
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <span
                              className={cn(
                                'flex h-4 w-4 items-center justify-center rounded border',
                                selected
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-border'
                              )}
                            >
                              {selected && <Check className="h-3 w-3" />}
                            </span>
                            <span className="font-medium">{label}</span>
                          </span>
                          {takenElsewhere && (
                            <span className="text-[10px] text-muted-foreground">
                              in “{owner?.label || 'another'}”
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
